import { forwardRef } from 'react';
import { cn } from '../../lib/utils.js';
import { useMagnetic } from '../../lib/useMagnetic.js';

const buttonVariants = {
  primary: 'bg-primary text-white border-primary hover:bg-transparent hover:text-primary dark:bg-white dark:text-gray-900 dark:border-white dark:hover:bg-transparent dark:hover:text-white',
  secondary: 'bg-transparent text-primary border-primary hover:bg-primary hover:text-white dark:text-white dark:border-white dark:hover:bg-white dark:hover:text-gray-900',
  white: 'bg-white text-primary border-white hover:bg-transparent hover:text-white dark:bg-white dark:text-gray-900 dark:border-white dark:hover:bg-transparent dark:hover:text-white',
  accent: 'bg-accent text-white border-accent hover:bg-accent-dark hover:border-accent-dark',
  ghost: 'bg-transparent text-primary border-transparent hover:bg-primary/10',
};

const buttonSizes = {
  sm: 'px-4 py-2 text-xs',
  md: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-base',
};

/**
 * Button component with magnetic effect and multiple variants
 */
export const Button = forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    magnetic = true,
    href,
    className,
    disabled = false,
    loading = false,
    icon,
    iconPosition = 'left',
    fullWidth = false,
    as,
    ...props
  },
  forwardedRef
) {
  const magneticRef = useMagnetic({ enabled: magnetic && !disabled });

  // Combine refs
  const ref = forwardedRef || magneticRef;

  const baseClasses = cn(
    'inline-flex items-center justify-center',
    'font-body font-semibold uppercase tracking-wider',
    'border-2 rounded-button',
    'transition-all duration-400',
    'cursor-pointer',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    buttonVariants[variant],
    buttonSizes[size],
    fullWidth && 'w-full',
    className
  );

  const content = (
    <>
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {icon && iconPosition === 'left' && !loading && (
        <span className="mr-2">{icon}</span>
      )}
      <span className="button__label">{children}</span>
      {icon && iconPosition === 'right' && (
        <span className="ml-2">{icon}</span>
      )}
    </>
  );

  // Render as link if href is provided
  if (href && !disabled) {
    const Component = as || 'a';
    return (
      <Component
        ref={ref}
        href={href}
        className={baseClasses}
        {...props}
      >
        {content}
      </Component>
    );
  }

  // Render as button
  return (
    <button
      ref={ref}
      className={baseClasses}
      disabled={disabled || loading}
      {...props}
    >
      {content}
    </button>
  );
});

export default Button;
